import { List, is } from "immutable";
import * as React from "react";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import { EditGroupForm } from "./EditGroupForm";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    groups: state.adminAbstracts.groups,
    loadingGroup: state.adminAbstracts.loadingGroup,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadGroup: (groupId: number) => dispatch(Actions.AdminAbstract.loadGroup(groupId)),
    loadGroups: (eventId: number) => dispatch(Actions.AdminAbstract.loadGroupsForEvent(eventId)),
    removeGroup: (group: M.Abstract.GradingGroup) => dispatch(Actions.AdminAbstract.removeGroup(group)),
    toggleLoading: (groupId: number, loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingGroup(groupId, loading)),
});

type OwnProps = RouteComponentProps<{
    groupId: string;
}> & {
    eventId: number;
};

type ConnectedState = Comp.Base.DefaultConnectedState & {
    groups: List<M.Abstract.GradingGroup>;
    loadingGroup: M.Generic.ItemLoadingState;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadGroup: (groupId: number) => void;
    loadGroups: (eventId: number) => void;
    removeGroup: (group: M.Abstract.GradingGroup) => void;
    toggleLoading: (groupId: number, loading: boolean) => void;
};

interface OwnState {
    group?: M.Abstract.GradingGroup;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class EditGroupComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _confirmDelete: Comp.Modal.ConfirmModal | null = null;
    private form: EditGroupForm | null = null;

    public constructor(props: any) {
        super(props);
        this.state = {
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        const groupId = this.getGroupId();
        await this.loadGroupFromState(true);

        if (groupId > 0)
            this.props.loadGroup(groupId);

        if (!this.props.groups.size)
            this.props.loadGroups(this.props.eventId);
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.groups, this.props.groups))
            this.loadGroupFromState();
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const groupId = this.getGroupId();
        const loading = !!this.props.loadingGroup[groupId];
        const groupListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Grading groups</a>
            : <Link to={Paths.AdminAbstractListGroups(this.props.eventId)}>Grading groups</Link>;

        const cancelLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Cancel</a>
            : <Link to={Paths.AdminAbstractListGroups(this.props.eventId)}>Cancel</Link>;

        const cancelWrap = <Comp.Generic.WrapperLess>
            {cancelLink}
            {groupId > 0
                ? <span className="btn btn-link mute" onClick={() => this.toggleDeleteConfirmation()} style={{ float: "right" }}>
                    <Comp.Generic.Icon icon="times" /> Delete
                </span>
                : null}
        </Comp.Generic.WrapperLess>;

        return <Comp.Page.AdminPage title={"Abstracts"}>

            {this.getErrorAlert()}

            <Comp.Generic.LoadingSpinner show={loading} />

            {groupId > 0
                ? <Comp.Modal.ConfirmModal
                    headerClassName="warning"
                    onProceed={() => this.handleDelete()}
                    title="Delete group"
                    ref={(ref) => this._confirmDelete = ref}
                >
                    <p>
                        Are you sure you want to delete this group?
                        Any associated reviewers and their abstract grades will also be deleted.
                    </p>
                </Comp.Modal.ConfirmModal>
                : null}

            <h1>
                <Link to={Paths.AdminAbstractList(this.props.eventId)}>Abstracts</Link> &raquo;{" "}
                {groupListLink} &raquo;{" "}
                {groupId === 0 ? "Add new group" : "Edit group"}
            </h1>

            <EditGroupForm
                api={this.props.api}
                cancelLink={cancelWrap}
                eventId={this.props.eventId}
                onSubmit={(model, body) => this.onSubmit(model, body)}
                parentGroups={this.props.groups.filter((x: M.Abstract.GradingGroup) => x.id !== groupId).toList()}
                ref={(ref) => this.form = ref}
            />

        </Comp.Page.AdminPage>;
    }

    private getGroupId(props?: Readonly<CombinedProps>) {
        return parseInt(props
            ? props.match.params.groupId
            : this.props.match.params.groupId, 10);
    }

    private async handleDelete() {
        if (this._confirmDelete === null)
            return;

        const group = this._confirmDelete.getRef() as M.Abstract.GradingGroup;
        if (group) {
            const result = await this.props.api.delete(Api.endpoints.admin.abstractGroup.group.withUrlParams({ id: group.id }));

            if (result.isSuccess) {
                this.props.removeGroup(group);
                this.props.notifySuccess(`Grading group <strong>${group.name}</strong> has been successfully deleted`);
                this.props.goto(Paths.AdminAbstractListGroups(this.props.eventId));
            }
            else {
                this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            }

            if (this._confirmDelete)
                this._confirmDelete.close();
        }
    }

    private lastPathIsFromListView() {
        return this.props.lastPath === `${Paths.AdminAbstractList(this.props.eventId)}/groups`;
    }

    private async loadGroupFromState(isInitialCheck?: boolean) {
        const groupId = this.getGroupId();
        const group = this.props.groups.find((x: M.Abstract.GradingGroup) => x.id === groupId);

        if (group) {
            await this.setStateAsync({ ...this.state, group });
            if (this.form)
                this.form.setFormData(group);
        }

        if (groupId > 0 && !group && isInitialCheck)
            this.props.toggleLoading(groupId, true);

        if (groupId > 0 && !group && !isInitialCheck) {
            this.props.goto(Paths.AdminAbstractListGroups(this.props.eventId));
        }
    }

    private async onSubmit(model: Forms.Abstract.GradingGroupData, body: any) {
        if (this.form)
            this.form.toggleProcessing(true);

        const result = await this.props.api.post(Api.endpoints.admin.abstractGroup.group.withUrlParams({ id: model.id }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted || !this.form)
            return;

        this.form.toggleProcessing(false);

        if (result.isSuccess) {
            this.props.notifySuccess(`Grading group <strong>${model.name}</strong> has been successfully saved.`);
            this.props.goto(Paths.AdminAbstractListGroups(this.props.eventId));
        }
        else {
            this.form.setFormErrors(M.Result.Result.errorsOrMessage(result));
        }
    }

    private toggleDeleteConfirmation() {
        if (this._confirmDelete !== null)
            this._confirmDelete.show(this.state.group);
    }
}

export const EditGroup: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(EditGroupComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/editGroup/EditGroup.tsx